import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React, { useCallback, useState } from 'react';
import * as Yup from 'yup';

import { addSchemaModalErrorMessages } from 'src/app/graph/subgraphsLayout/subgraphsPage/addSubgraphModal/shared';
import { InputCharCount } from 'src/components/InputCharCount';
import { API_NAME_MAX_CHAR } from 'src/lib/graphOnboarding/constants';

const validationSchema = Yup.object().shape({
  subgraphName: Yup.string()
    .required('Please provide a name for your proposed Subgraph.')
    .min(2, addSchemaModalErrorMessages.subgraphName.min)
    .max(API_NAME_MAX_CHAR, addSchemaModalErrorMessages.subgraphName.max)
    .matches(
      /^[a-zA-Z]/,
      addSchemaModalErrorMessages.subgraphName.startWithLetter,
    )
    .matches(
      /^[a-zA-Z0-9_-]*$/,
      addSchemaModalErrorMessages.subgraphName.contents,
    ),
});

type FormValues = Yup.InferType<typeof validationSchema>;

export const AddProposalSubgraphModal = ({
  isOpen,
  onClose,
  onAddSubgraph,
  existingSubgraphNames,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAddSubgraph: (newSubgraphName: string) => void;
  existingSubgraphNames: string[];
}) => {
  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    resetForm,
    isValid,
  } = useFormik<FormValues>({
    validateOnMount: true,
    validateOnBlur: true,
    validateOnChange: true,
    initialValues: {
      subgraphName: '',
    },
    onSubmit: ({ subgraphName }) => {
      if (existingSubgraphNames.includes(subgraphName)) {
        setShowSubgraphAlreadyExistsError(true);
      } else {
        onClose();
        onAddSubgraph(subgraphName);
        resetForm({});
      }
    },
    validationSchema,
  });

  const [showSubgraphAlreadyExistsError, setShowSubgraphAlreadyExistsError] =
    useState(false);

  const onCloseModal = useCallback(() => {
    onClose();
    resetForm({});
    setShowSubgraphAlreadyExistsError(false);
  }, [onClose, resetForm]);

  return (
    <Modal size="xl" isOpen={isOpen} onClose={onCloseModal}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Add a subgraph</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <FormControl
            isRequired
            isInvalid={
              (!!errors.subgraphName && !!touched.subgraphName) ||
              showSubgraphAlreadyExistsError
            }
          >
            <FormLabel>Subgraph name</FormLabel>
            <Input
              name="subgraphName"
              placeholder={'e.g. "accounts", "billing", etc.'}
              value={values.subgraphName}
              onBlur={handleBlur}
              onChange={handleChange}
              rightElement={
                <InputCharCount
                  count={values.subgraphName.length}
                  maxCount={API_NAME_MAX_CHAR}
                />
              }
            />
            <FormErrorMessage>
              {showSubgraphAlreadyExistsError
                ? 'This subgraph already exists in this proposal.'
                : errors.subgraphName}
            </FormErrorMessage>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button variant="secondary" onClick={onCloseModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              isDisabled={!isValid}
              type="submit"
              onClick={() => handleSubmit()}
            >
              Add subgraph
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
